
import { ProductGroup } from "@/models/base/product-group";
import Vue from "vue";
import AppContextMenu from "./ProductGroupContextMenu.vue";
import AppProductGroupEditProperties from "./ProductGroupEditProperties.vue";

enum DropPosition {
	NONE,
	TOP,
	INSIDE,
	BOTTOM
}

export default Vue.extend({
	name: "AppProductGroupEditNode",
	data(){
		return {
			draggingPosition: DropPosition.NONE,
			contextMenuOpened: false,
			contextMenuX: 0,
			contextMenuY: 0,
			isRenaming: false,
			newName: "",
			draggedItemHasChildren: false,
			editPropertiesOpened: false
		};
	},
	props: {
		value: {
			type: Object as ()=> ProductGroup,
			required: true
		},
		path: {
			type: String,
			required: true
		},
		isFirstItem: Boolean,
		isTopLevelGroup: Boolean
	},
	computed: {
		isDragging():boolean{
			return this.draggingPosition == DropPosition.NONE;
		},
		insideAllowed():boolean {
			if (this.draggedItemHasChildren) {
				return false;
			}
			return this.isTopLevelGroup;
		},
		isDraggingOverTop():boolean{
			return this.draggingPosition == DropPosition.TOP;
		},
		isDraggingInside():boolean {
			if (!this.insideAllowed) {
				return false;
			}
			return this.draggingPosition == DropPosition.INSIDE;
		},
		isDraggingOverBottom():boolean{
			return this.draggingPosition == DropPosition.BOTTOM;
		},
		canAddSubGroup():boolean{
			let parts = this.path.split("/");
			if (parts.length > 1) {
				return false;
			}
			return true;
		},
		canEdit():boolean {
			return ProductGroup.canEditGroups();
		},
		isDraggable():"true" | "false" {
			if (this.canEdit) return "true";
			return "false";
		}
	},
	methods: {
		openContextMenu(event:MouseEvent){
			this.contextMenuOpened = true;
			this.contextMenuX = event.x;
			this.contextMenuY = event.y;
		},
		createNewGroup(){
			let group = new ProductGroup();
			group.Name = this.$ct("new-product-group-name").toString();
			this.value.addChild(group);
			this.contextMenuOpened = false;
		},
		deleteGroup(group:ProductGroup){
			if (!this.canAddSubGroup) return;
			this.value.removeChild(group);
			this.contextMenuOpened = false;
		},
		async deleteSelf(){
			let confirm = await this.$wf.confirm(this.$ct("cofirm-delete-group",
				{group: this.value.Name}), this.$t("common.yes"), this.$t("common.no"), this.$ct("common.warning"), "warning");
			if (!confirm) return;
			this.$emit("remove");
		},
		async rename(){
			this.newName = this.value.Name;
			this.isRenaming = true;
			await this.$nextTick();
			let ref = this.$refs.renameInput as any;
			if (!ref) return;
			ref.focus();
		},
		onRenameBlur(){
			this.newName = this.newName.trim();
			if (this.newName != "") {
				this.value.Name = this.newName;
			}
			this.isRenaming = false;
		},
		onRenameEscape(){
			this.newName = this.value.Name;
			this.isRenaming = false;
		},
		computeDragPosition(event:DragEvent):void {
			if (!event.target) throw new Error("No target");
			let target = event.target as HTMLElement;
			let box = target.getBoundingClientRect();
			let y = event.pageY - box.top;
			if (!this.insideAllowed) {
				let boxPart = box.height / 2;
				if (y < boxPart) {
					this.draggingPosition = DropPosition.TOP;
				}else {
					this.draggingPosition = DropPosition.BOTTOM;
				}
				return;
			}
			let boxPart = box.height / 3;
			if (y < boxPart) {
				this.draggingPosition = DropPosition.TOP;
			}else if (y < (boxPart * 2)) {
				this.draggingPosition = DropPosition.INSIDE;
			}else{
				this.draggingPosition = DropPosition.BOTTOM;
			}
		},
		canDrop(path:string):boolean{
			if (!path.startsWith("product-group-move")) return false;
			path = path.split(":")[1];
			if (this.path.startsWith(path)){
				return false;
			}
			if (!this.isTopLevelGroup && this.draggedItemHasChildren) {
				return false;
			}
			return true;
		},
		onDragStart(event:DragEvent){
			if (!event.dataTransfer) return;
			event.dataTransfer.setData("text", `product-group-move:${this.path}`);
			event.dataTransfer.setData("has-children", `${this.value.Children.length > 0}`);
		},
		onDragOver(event:DragEvent){
			if (!event.dataTransfer) return;
			let path = event.dataTransfer.getData("text");
			this.draggedItemHasChildren = event.dataTransfer.getData("has-children") == "true";
			if (this.canDrop(path)){
				this.computeDragPosition(event);
			}
		},
		async onDrop(event:DragEvent){
			let confirm = await this.$wf.confirm(this.$ct("cofirm-drop-group",
				{group: this.value.Name}), this.$t("common.yes"), this.$t("common.no"), this.$ct("common.warning"), "warning");
			if (!confirm) return;
			if (!event.dataTransfer) return;
			let path = event.dataTransfer.getData("text");
			if (!this.canDrop(path)) return;
			path = path.split(":")[1];
			this.computeDragPosition(event);
			if (this.draggingPosition == DropPosition.TOP) {
				this.$emit("move", path, this.path);
			}else if (this.draggingPosition == DropPosition.INSIDE){
				this.$emit("move", path, `${this.path}/${this.value.Children.length}`);
			}else{
				let parts = this.path.split("/").map(n=>parseInt(n));
				parts[parts.length - 1] = parts[parts.length - 1] + 1;
				this.$emit("move", path, parts.join("/"));
			}
			this.draggingPosition = DropPosition.NONE;
		},
		onDragLeave(event:DragEvent){
			this.draggingPosition = DropPosition.NONE;
		},
		propagateMove(from:string, to:string){
			this.$emit("move", from, to);
		}
	},
	components: {
		AppContextMenu,
		AppProductGroupEditProperties
	},
	i18nEx: {
		componentPrefix: "components.products"
	}
});
